import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Users, Search, Plus, Pencil, Trash2, KeyRound, Shield, Clock, RefreshCw, X, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_COLORS = {
  admin: 'bg-red-100 text-red-800',
  investor: 'bg-blue-100 text-blue-800',
  client: 'bg-green-100 text-green-800',
  staff: 'bg-purple-100 text-purple-800',
};

const AdminSettingsPage = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('all');
  const [auditLogs, setAuditLogs] = useState([]);

  // Dialog state
  const [editUser, setEditUser] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [resetPwResult, setResetPwResult] = useState(null);
  const [saving, setSaving] = useState(false);

  // Create form
  const [newUser, setNewUser] = useState({
    email: '', first_name: '', last_name: '', password: '',
    role: 'investor', phone: '', reference_id: '', risk_participant_code: '', client_id: ''
  });

  useEffect(() => { fetchUsers(); }, [roleFilter, activeFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (roleFilter !== 'all') params.append('role', roleFilter);
      if (activeFilter !== 'all') params.append('is_active', activeFilter === 'active');
      if (search) params.append('search', search);
      params.append('limit', '200');
      const res = await axios.get(`${API_URL}/api/admin/users?${params}`, { headers });
      setUsers(res.data.users || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Failed to load users');
    } finally { setLoading(false); }
  };

  const fetchAuditLog = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/audit-log?limit=100`, { headers });
      setAuditLogs(res.data.logs || []);
    } catch (err) { toast.error('Failed to load audit log'); }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers();
  };

  const handleEdit = (user) => {
    setEditUser({ ...user });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/admin/users/${editUser.id}`, {
        first_name: editUser.first_name,
        last_name: editUser.last_name,
        email: editUser.email,
        phone: editUser.phone,
        role: editUser.role,
        is_active: editUser.is_active,
        reference_id: editUser.reference_id || null,
        risk_participant_code: editUser.risk_participant_code || null,
        client_id: editUser.client_id || null,
      }, { headers });
      toast.success('User updated', { description: `${res.data.changes?.length || 0} field(s) changed` });
      setEditOpen(false);
      fetchUsers();
    } catch (err) {
      toast.error('Update failed', { description: err.response?.data?.detail });
    } finally { setSaving(false); }
  };

  const handleCreate = async () => {
    if (!newUser.email || !newUser.first_name || !newUser.last_name || !newUser.password) {
      toast.error('Fill in all required fields');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/users`, newUser, { headers });
      toast.success(res.data?.linked_login ? 'Investor profile linked' : 'User created',
        res.data?.linked_login ? { description: res.data.message, duration: 10000 } : undefined);
      setCreateOpen(false);
      setNewUser({ email: '', first_name: '', last_name: '', password: '', role: 'investor', phone: '', reference_id: '', risk_participant_code: '', client_id: '' });
      fetchUsers();
    } catch (err) {
      toast.error('Create failed', { description: err.response?.data?.detail });
    } finally { setSaving(false); }
  };

  const handleDelete = async (userId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/users/${userId}`, { headers });
      toast.success('User deleted');
      setDeleteConfirm(null);
      fetchUsers();
    } catch (err) {
      toast.error('Delete failed', { description: err.response?.data?.detail });
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/users/${userId}/reset-password`, {}, { headers });
      setResetPwResult(res.data);
      toast.success('Password reset');
    } catch (err) {
      toast.error('Reset failed', { description: err.response?.data?.detail });
    }
  };

  const filteredUsers = useMemo(() => users, [users]);

  const formatDate = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return d; }
  };

  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">System Settings</h1>
          <p className="text-slate-600">Manage user accounts, roles, and access</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="gap-2 bg-[#1D2D2A] hover:bg-[#243D37]" data-testid="create-user-btn">
          <Plus className="h-4 w-4" /> Create User
        </Button>
      </div>

      <Tabs defaultValue="users" className="w-full">
        <TabsList>
          <TabsTrigger value="users" className="gap-2"><Users className="h-4 w-4" />Users</TabsTrigger>
          <TabsTrigger value="audit" className="gap-2" onClick={fetchAuditLog}><Clock className="h-4 w-4" />Audit Log</TabsTrigger>
          <TabsTrigger value="security" className="gap-2"><Shield className="h-4 w-4" />Security & 2FA</TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4">
          <Card>
            <CardContent className="pt-4">
              <form onSubmit={handleSearch} className="flex items-center gap-3 mb-4">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input placeholder="Search by name, email, reference ID..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="user-search" />
                </div>
                <Select value={roleFilter} onValueChange={setRoleFilter}>
                  <SelectTrigger className="w-36" data-testid="role-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="investor">Investor</SelectItem>
                    <SelectItem value="client">Client</SelectItem>
                    <SelectItem value="staff">Staff</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={activeFilter} onValueChange={setActiveFilter}>
                  <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
                <Button type="submit" variant="outline" size="sm"><Search className="h-4 w-4" /></Button>
              </form>

              <div className="text-sm text-slate-500 mb-2">{total} users found</div>

              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Reference ID</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Last Login</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow><TableCell colSpan={7} className="text-center py-8"><RefreshCw className="h-5 w-5 animate-spin mx-auto" /></TableCell></TableRow>
                    ) : filteredUsers.length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">No users found</TableCell></TableRow>
                    ) : filteredUsers.map(user => (
                      <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                        <TableCell className="font-medium">{user.first_name} {user.last_name}</TableCell>
                        <TableCell className="text-sm">{user.email}</TableCell>
                        <TableCell className="font-mono text-sm">{user.reference_id || user.risk_participant_code || '-'}</TableCell>
                        <TableCell><Badge className={ROLE_COLORS[user.role] || 'bg-gray-100'}>{user.role}</Badge></TableCell>
                        <TableCell>
                          {user.is_active !== false
                            ? <Badge className="bg-green-100 text-green-800">Active</Badge>
                            : <Badge className="bg-red-100 text-red-800">Disabled</Badge>}
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">{formatDate(user.last_login)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(user)} data-testid={`edit-user-${user.id}`}><Pencil className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => handleResetPassword(user.id)} data-testid={`reset-pw-${user.id}`}><KeyRound className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => setDeleteConfirm(user)} data-testid={`delete-user-${user.id}`}><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Log Tab */}
        <TabsContent value="audit">
          <Card>
            <CardHeader><CardTitle>Audit Trail</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Target</TableHead>
                    <TableHead>Performed By</TableHead>
                    <TableHead>Changes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditLogs.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-500">No audit records</TableCell></TableRow>
                  ) : auditLogs.map(log => (
                    <TableRow key={log.id}>
                      <TableCell className="text-sm">{formatDate(log.timestamp)}</TableCell>
                      <TableCell><Badge variant="outline">{log.action?.replace(/_/g, ' ')}</Badge></TableCell>
                      <TableCell className="text-sm">{log.target_email || log.target_user_id?.substring(0, 8)}</TableCell>
                      <TableCell className="text-sm">{log.performed_by_email}</TableCell>
                      <TableCell className="text-sm max-w-xs truncate">
                        {(log.changes || []).map((c, i) => (
                          <span key={i} className="mr-2">
                            <span className="font-medium">{c.field}</span>: {String(c.old || '').substring(0, 15)} → {String(c.new || '').substring(0, 15)}
                          </span>
                        ))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="space-y-4">
          <SecuritySection token={token} headers={headers} />
        </TabsContent>
      </Tabs>

      {/* Edit User Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>Update user details. Changes are audited.</DialogDescription>
          </DialogHeader>
          {editUser && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1"><Label>First Name</Label><Input value={editUser.first_name || ''} onChange={(e) => setEditUser({...editUser, first_name: e.target.value})} data-testid="edit-first-name" /></div>
              <div className="space-y-1"><Label>Last Name</Label><Input value={editUser.last_name || ''} onChange={(e) => setEditUser({...editUser, last_name: e.target.value})} data-testid="edit-last-name" /></div>
              <div className="col-span-2 space-y-1"><Label>Email</Label><Input value={editUser.email || ''} onChange={(e) => setEditUser({...editUser, email: e.target.value})} data-testid="edit-email" /></div>
              <div className="space-y-1"><Label>Phone</Label><Input value={editUser.phone || ''} onChange={(e) => setEditUser({...editUser, phone: e.target.value})} /></div>
              <div className="space-y-1">
                <Label>Role</Label>
                <Select value={editUser.role} onValueChange={(v) => setEditUser({...editUser, role: v})}>
                  <SelectTrigger data-testid="edit-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="investor">Investor</SelectItem>
                    <SelectItem value="client">Client</SelectItem>
                    <SelectItem value="staff">Staff</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label>Reference ID</Label><Input value={editUser.reference_id || ''} onChange={(e) => setEditUser({...editUser, reference_id: e.target.value})} placeholder="PTC-0001-00" /></div>
              <div className="space-y-1"><Label>RP Code</Label><Input value={editUser.risk_participant_code || ''} onChange={(e) => setEditUser({...editUser, risk_participant_code: e.target.value})} placeholder="01-00" /></div>
              <div className="col-span-2 flex items-center gap-2 pt-2">
                <Checkbox checked={editUser.is_active !== false} onCheckedChange={(v) => setEditUser({...editUser, is_active: v})} data-testid="edit-active" />
                <Label>Account Active</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={saving} className="bg-[#1D2D2A]" data-testid="save-edit-btn">{saving ? 'Saving...' : 'Save Changes'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create User</DialogTitle>
            <DialogDescription>Create a new user account.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1"><Label>First Name *</Label><Input value={newUser.first_name} onChange={(e) => setNewUser({...newUser, first_name: e.target.value})} data-testid="create-first-name" /></div>
            <div className="space-y-1"><Label>Last Name *</Label><Input value={newUser.last_name} onChange={(e) => setNewUser({...newUser, last_name: e.target.value})} data-testid="create-last-name" /></div>
            <div className="col-span-2 space-y-1"><Label>Email *</Label><Input type="email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} data-testid="create-email" /></div>
            <div className="space-y-1"><Label>Password *</Label><Input type="password" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} data-testid="create-password" /></div>
            <div className="space-y-1">
              <Label>Role</Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser({...newUser, role: v})}>
                <SelectTrigger data-testid="create-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="investor">Investor</SelectItem>
                  <SelectItem value="client">Client</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Phone</Label><Input value={newUser.phone} onChange={(e) => setNewUser({...newUser, phone: e.target.value})} /></div>
            <div className="space-y-1"><Label>Reference ID</Label><Input value={newUser.reference_id} onChange={(e) => setNewUser({...newUser, reference_id: e.target.value})} placeholder="PTC-0001-00" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} className="bg-[#1D2D2A]" data-testid="create-submit-btn">{saving ? 'Creating...' : 'Create User'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-red-500" />Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deleteConfirm?.email}</strong> ({deleteConfirm?.first_name} {deleteConfirm?.last_name})? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm?.id)} data-testid="confirm-delete-btn">Delete User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Reset Result */}
      <Dialog open={!!resetPwResult} onOpenChange={() => setResetPwResult(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Password Reset</DialogTitle>
            <DialogDescription>The user's password has been reset. Share this temporary password securely.</DialogDescription>
          </DialogHeader>
          <div className="bg-slate-50 rounded-lg p-4 font-mono text-center text-lg select-all" data-testid="temp-password">
            {resetPwResult?.temporary_password}
          </div>
          <p className="text-sm text-amber-600">The user will be required to change this on next login.</p>
          <DialogFooter>
            <Button onClick={() => setResetPwResult(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSettingsPage;

// ═══════════════════════════════════════════════════════════════════
// Security & 2FA section — admin can enrol/disable their own TOTP and
// view the platform-wide 2FA audit log.
// ═══════════════════════════════════════════════════════════════════
const SecuritySection = ({ token, headers }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [qr, setQr] = useState(null);
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [audit, setAudit] = useState([]);

  const fetchStatus = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/auth/2fa/status`, { headers });
      setStatus(r.data);
    } catch { /* noop */ }
  };
  const fetchAudit = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/2fa/audit?limit=50`, { headers });
      setAudit(r.data.rows || []);
    } catch { /* noop */ }
  };
  useEffect(() => { fetchStatus(); fetchAudit(); /* eslint-disable-next-line */ }, []);

  const beginSetup = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/auth/2fa/setup`, {}, { headers });
      setQr(`data:image/png;base64,${r.data.qr_png_base64}`);
      setSecret(r.data.secret_plaintext);
      setBackupCodes(null);
      setCode('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start 2FA setup');
    } finally { setLoading(false); }
  };
  const confirmSetup = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/auth/2fa/verify-setup`, { code }, { headers });
      setBackupCodes(r.data.backup_codes || []);
      setCode('');
      fetchStatus();
      fetchAudit();
      toast.success('Two-factor authentication activated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code');
    } finally { setLoading(false); }
  };
  const disable = async () => {
    const c = prompt('Enter your current authenticator code to disable 2FA:');
    if (!c) return;
    const p = prompt('Confirm your account password:');
    if (!p) return;
    try {
      await axios.post(`${API_URL}/api/auth/2fa/disable`, { code: c, password: p }, { headers });
      toast.success('2FA disabled');
      setQr(null); setSecret(''); setBackupCodes(null);
      fetchStatus(); fetchAudit();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to disable');
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Your two-factor authentication</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {status && (
            <div className="flex items-center gap-3">
              <Badge className={status.totp_enabled ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>
                {status.totp_enabled ? 'Enabled' : 'Not enabled'}
              </Badge>
              {status.totp_enabled && (
                <span className="text-sm text-slate-600">
                  Backup codes remaining: <strong>{status.backup_codes_remaining}</strong>
                </span>
              )}
            </div>
          )}

          {!status?.totp_enabled && !qr && (
            <Button onClick={beginSetup} disabled={loading} data-testid="admin-enable-2fa-btn">
              {loading ? 'Loading…' : 'Enable 2FA'}
            </Button>
          )}
          {status?.totp_enabled && !qr && (
            <Button variant="outline" onClick={disable} data-testid="admin-disable-2fa-btn">Disable 2FA</Button>
          )}

          {qr && !backupCodes && (
            <div className="space-y-3 max-w-md">
              <p className="text-sm text-slate-700">
                Scan this QR code with an authenticator app (Google Authenticator, Authy, 1Password, etc.).
              </p>
              <img src={qr} alt="2FA QR" className="w-48 h-48 border rounded" />
              <div className="text-xs text-slate-500">
                Or enter this secret manually:
                <code className="ml-2 bg-slate-100 px-2 py-1 rounded font-mono text-sm">{secret}</code>
              </div>
              <div className="space-y-1">
                <Label>6-digit code</Label>
                <Input value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} placeholder="123 456" data-testid="admin-2fa-code-input" />
              </div>
              <div className="flex gap-2">
                <Button onClick={confirmSetup} disabled={code.length !== 6 || loading} data-testid="admin-2fa-verify-btn">
                  {loading ? 'Verifying…' : 'Verify & activate'}
                </Button>
                <Button variant="outline" onClick={() => { setQr(null); setSecret(''); }}>Cancel</Button>
              </div>
            </div>
          )}

          {backupCodes && (
            <div className="space-y-3 max-w-md" data-testid="admin-backup-codes-panel">
              <div className="p-3 rounded border-2 border-emerald-500 bg-emerald-50">
                <p className="text-sm font-semibold text-emerald-800">✓ Two-factor authentication is now active</p>
                <p className="text-xs text-emerald-700 mt-1">
                  Save these backup codes somewhere safe — this is the ONLY time they will be shown. Each code works exactly once.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-slate-50 p-3 rounded border">
                {backupCodes.map((c, i) => <code key={i} className="text-center py-1">{c}</code>)}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => {
                  navigator.clipboard.writeText(backupCodes.join('\n'));
                  toast.success('Backup codes copied');
                }}>Copy all</Button>
                <Button size="sm" onClick={() => { setQr(null); setBackupCodes(null); }}>I have saved my codes</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Platform-wide 2FA activity (last 50)</CardTitle>
        </CardHeader>
        <CardContent>
          {audit.length === 0 ? (
            <p className="text-sm text-slate-500">No 2FA events yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Detail</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {audit.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-xs whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</TableCell>
                    <TableCell className="text-sm">{r.user_email || r.user_id?.slice(0, 8)}</TableCell>
                    <TableCell className="text-xs">{r.user_role}</TableCell>
                    <TableCell className="text-xs font-mono">{r.action}</TableCell>
                    <TableCell className="text-xs text-slate-600">
                      {r.method && `method=${r.method}`}
                      {r.code_count != null && ` codes=${r.code_count}`}
                      {r.remaining_codes != null && ` remaining=${r.remaining_codes}`}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
